import { useEffect, useRef, useState } from "react";
import { MessageSquare, Send, User, X } from "lucide-react";
import { adminApi } from "@/lib/api";

export default function LiveChats() {
    const [sessions, setSessions] = useState([]);
    const [active, setActive] = useState(null);
    const [detail, setDetail] = useState(null);
    const [reply, setReply] = useState("");
    const [loading, setLoading] = useState(true);
    const scrollRef = useRef(null);

    const fetchSessions = async () => {
        try {
            const r = await adminApi().get("/admin/chats");
            setSessions(r.data);
        } catch {}
        setLoading(false);
    };

    const fetchDetail = async (sid) => {
        try {
            const r = await adminApi().get(`/admin/chats/${sid}`);
            setDetail(r.data);
        } catch {}
    };

    useEffect(() => { fetchSessions(); const t = setInterval(fetchSessions, 8000); return () => clearInterval(t); }, []);
    useEffect(() => { if (active) { fetchDetail(active); const t = setInterval(() => fetchDetail(active), 4000); return () => clearInterval(t); } }, [active]);
    useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9 }); }, [detail]);

    const send = async () => {
        if (!reply.trim() || !active) return;
        try {
            await adminApi().post(`/admin/chats/${active}/reply`, { text: reply.trim() });
            setReply("");
            fetchDetail(active);
        } catch {}
    };

    return (
        <div data-testid="live-chats-panel" className="bg-white border border-cream-border min-h-[60vh] grid grid-cols-1 md:grid-cols-3">
            {/* Sessions list */}
            <div className="border-r border-cream-border max-h-[70vh] overflow-y-auto">
                <div className="bg-navy text-gold px-4 py-3 uppercase text-xs tracking-wider font-outfit font-semibold sticky top-0">Conversations ({sessions.length})</div>
                {loading && <p className="p-6 text-center text-muted-foreground italic text-sm">Loading…</p>}
                {!loading && sessions.length === 0 && <p className="p-6 text-center text-muted-foreground italic text-sm">No chats yet.</p>}
                {sessions.map((s) => (
                    <button
                        key={s.session_id}
                        data-testid={`chat-session-${s.session_id}`}
                        onClick={() => setActive(s.session_id)}
                        className={`w-full text-left px-4 py-3 border-b border-cream-border hover:bg-cream-light font-outfit ${active === s.session_id ? "bg-cream-light border-l-2 border-l-gold" : ""}`}
                    >
                        <div className="flex items-center justify-between mb-1">
                            <div className="text-sm font-semibold text-navy truncate">{s.visitor_name || "Visitor"}</div>
                            {s.has_handoff && <span className="text-[9px] uppercase tracking-wider bg-gold text-navy px-1.5 py-0.5 font-semibold">Handoff</span>}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{s.visitor_contact}</div>
                        <div className="text-xs text-navy/70 mt-1 line-clamp-1 italic">"{s.last_user}"</div>
                        <div className="text-[10px] text-muted-foreground mt-1">{new Date(s.last_at).toLocaleString()} · {s.count} msgs</div>
                    </button>
                ))}
            </div>

            {/* Conversation panel */}
            <div className="md:col-span-2 flex flex-col">
                {!detail ? (
                    <div className="flex-1 flex items-center justify-center text-muted-foreground font-outfit italic text-sm">
                        <div className="text-center">
                            <MessageSquare className="w-10 h-10 mx-auto mb-3 text-gold/50" />
                            Select a conversation to view & reply
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="px-5 py-3 border-b border-cream-border bg-cream-light flex items-center justify-between">
                            <div>
                                <div className="text-sm font-semibold text-navy font-outfit">{detail.handoff?.visitor_name || detail.messages[0]?.visitor_name || "Visitor"}</div>
                                <div className="text-xs text-muted-foreground font-outfit">{detail.handoff?.visitor_contact || detail.messages[0]?.visitor_contact}</div>
                            </div>
                            {detail.handoff && <span className="text-[10px] uppercase tracking-wider bg-gold text-navy px-2 py-1 font-semibold">Handoff requested</span>}
                        </div>

                        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-cream-light max-h-[55vh]">
                            {detail.messages.map((m, i) => (
                                <div key={i}>
                                    {m.user && (
                                        <div className="flex justify-end mb-1"><div className="max-w-[75%] px-3 py-2 text-sm bg-navy text-cream font-outfit">{m.user}</div></div>
                                    )}
                                    {m.bot && (
                                        <div className="flex justify-start mb-1"><div className="max-w-[75%] px-3 py-2 text-sm bg-white border border-cream-border text-navy font-outfit"><span className="text-[10px] uppercase tracking-wider text-gold-dark block mb-1">Signa</span>{m.bot}</div></div>
                                    )}
                                    {m.agent && (
                                        <div className="flex justify-start mb-1"><div className="max-w-[75%] px-3 py-2 text-sm bg-gold/15 border border-gold text-navy font-outfit"><span className="text-[10px] uppercase tracking-wider text-gold-dark block mb-1">You</span>{m.agent}</div></div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="p-3 border-t border-cream-border bg-white flex gap-2">
                            <input
                                data-testid="agent-reply-input"
                                value={reply}
                                onChange={(e) => setReply(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && send()}
                                placeholder="Type your reply to the visitor…"
                                className="flex-1 border border-cream-border px-3 py-2 text-sm font-outfit focus:outline-none focus:border-gold"
                            />
                            <button data-testid="agent-reply-send" onClick={send} className="bg-gold text-navy px-4 font-outfit font-semibold uppercase tracking-wider text-xs hover:bg-gold-light flex items-center gap-2">
                                <Send className="w-3 h-3" /> Send
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
